import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  SetStateAction,
} from 'react'

import { ListToolbar } from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import {
  useScheduleListPageClamp,
  useScheduleListPagination,
} from '../../hooks'
import { useScheduleI18n } from '../../i18n'
import type { Schedule, ScheduleId } from '../../types'
import { tokenVar } from '../../utils'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { ScheduleStatusBadge } from '../ScheduleStatusBadge'
import { PlusIcon, SearchIcon, TrashIcon } from '../shared'

export interface ScheduleListError {
  code?: string
  message: string
}

export interface ScheduleCardStatus {
  label: string
  tone?: 'open' | 'closed' | 'holiday'
}

export type ScheduleListPageSize = number | 'All'

export interface SchedulesListPageStrings {
  title: string
  subtitle: string
  newSchedule: string
  loading: string
  emptyTitle: string
  emptyDescription: string
  emptySearchTitle?: string
  emptySearchDescription?: string
  deleteSchedule: string
}

export interface SchedulesListPageViewProps {
  schedules: Schedule[]
  isLoading?: boolean
  loadingScheduleIds?: ReadonlySet<ScheduleId>
  error?: ScheduleListError | null
  strings: SchedulesListPageStrings
  canCreate?: boolean
  onCreate?: () => void
  onScheduleClick?: (scheduleId: ScheduleId) => void
  getScheduleHref?: (scheduleId: ScheduleId) => string | undefined
  onDeleteSchedule?: (schedule: Schedule) => void
  getScheduleStatus?: (schedule: Schedule) => ScheduleCardStatus | null
  getScheduleDescription?: (schedule: Schedule) => string | undefined
  getMetaLeft?: (schedule: Schedule) => string
  getMetaRight?: (schedule: Schedule) => string
  createIcon?: ReactNode
  deleteIcon?: ReactNode
  totalCount?: number
  currentPage?: number
  pageSize?: ScheduleListPageSize
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: ScheduleListPageSize) => void
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
}

const tokenizedSvgColorStyles = {
  '& svg': {
    color: 'currentColor',
  },
  '& svg path': {
    stroke: 'currentColor',
  },
}

const SCHEDULE_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500]

const formatErrorCode = (
  code: string | undefined,
  fallback: string,
): string => {
  if (!code) return fallback
  return code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const formatUpdatedAt = (updatedAt: string, fallback: string): string => {
  try {
    return new Date(updatedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return fallback
  }
}

const getStatusBorderColor = (tone: ScheduleCardStatus['tone']) => {
  if (tone === 'open') {
    return 'green.solid'
  }

  if (tone === 'closed') {
    return 'red.solid'
  }

  if (tone === 'holiday') {
    return 'blue.solid'
  }

  return 'border.default'
}

const ScheduleCard = ({
  schedule,
  isSummaryLoading,
  summaryLoadingLabel,
  deleteLabel,
  onScheduleClick,
  getScheduleHref,
  onDeleteSchedule,
  getScheduleStatus,
  getScheduleDescription,
  getMetaLeft,
  getMetaRight,
  deleteIcon,
}: {
  schedule: Schedule
  isSummaryLoading: boolean
  summaryLoadingLabel: string
  deleteLabel: string
  onScheduleClick?: (scheduleId: ScheduleId) => void
  getScheduleHref?: (scheduleId: ScheduleId) => string | undefined
  onDeleteSchedule?: (schedule: Schedule) => void
  getScheduleStatus?: (schedule: Schedule) => ScheduleCardStatus | null
  getScheduleDescription?: (schedule: Schedule) => string | undefined
  getMetaLeft: (schedule: Schedule) => string
  getMetaRight: (schedule: Schedule) => string
  deleteIcon?: ReactNode
}) => {
  const status = isSummaryLoading
    ? null
    : (getScheduleStatus?.(schedule) ?? null)
  const borderLeftColor = getStatusBorderColor(status?.tone)
  const description = getScheduleDescription?.(schedule) ?? schedule.description
  const scheduleHref = getScheduleHref?.(schedule.id)
  const isClickable = !!onScheduleClick || !!scheduleHref

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onScheduleClick || scheduleHref) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onScheduleClick(schedule.id)
    }
  }

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !onScheduleClick ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    event.preventDefault()
    onScheduleClick(schedule.id)
  }

  return (
    <Box
      data-schedule-card-id={schedule.id}
      aria-busy={isSummaryLoading || undefined}
      bg="bg.default"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.default"
      borderLeftWidth="4px"
      borderLeftColor={borderLeftColor}
      boxShadow="sm"
      position="relative"
      minH="136px"
      p={6}
      display="flex"
      flexDirection="column"
      cursor={isClickable ? 'pointer' : 'default'}
      _hover={isClickable ? { boxShadow: 'md' } : undefined}
      onClick={
        onScheduleClick && !scheduleHref
          ? () => onScheduleClick(schedule.id)
          : undefined
      }
      onKeyDown={handleKeyDown}
      role={onScheduleClick && !scheduleHref ? 'button' : undefined}
      tabIndex={onScheduleClick && !scheduleHref ? 0 : undefined}
    >
      <Flex justify="space-between" align="flex-start" gap={3} mb={4}>
        <Flex align="center" gap={3} flex={1} minW={0}>
          <Box minW={0}>
            <Flex align="center" gap={3} wrap="wrap">
              {scheduleHref ? (
                <Link
                  href={scheduleHref}
                  onClick={handleLinkClick}
                  fontSize="xl"
                  fontWeight="bold"
                  color="fg.default"
                  lineClamp={1}
                  textDecoration="none"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                  }}
                >
                  {schedule.name}
                </Link>
              ) : (
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  color="fg.default"
                  lineClamp={1}
                >
                  {schedule.name}
                </Text>
              )}
              {status && (
                <ScheduleStatusBadge
                  label={status.label}
                  tone={status.tone}
                  size="sm"
                />
              )}
            </Flex>
            <Text
              fontSize="sm"
              color="fg.muted"
              mt={0.5}
              lineClamp={1}
              minH="20px"
              visibility={description ? 'visible' : 'hidden'}
            >
              {description || ' '}
            </Text>
          </Box>
        </Flex>
        {onDeleteSchedule && (
          <IconButton
            aria-label={deleteLabel}
            size="sm"
            variant="ghost"
            borderRadius="8px"
            colorPalette="error"
            color="red.fg"
            _hover={{ bg: 'red.subtle', borderRadius: '8px' }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onDeleteSchedule(schedule)
            }}
            position="relative"
            zIndex={1}
            flexShrink={0}
            css={tokenizedSvgColorStyles}
          >
            {deleteIcon ?? <TrashIcon size={18} strokeWidth={1.5} />}
          </IconButton>
        )}
      </Flex>

      <Flex justify="space-between" align="center" gap={3} mt="auto">
        <Text fontSize="sm" color="fg.muted" lineClamp={1}>
          {isSummaryLoading ? summaryLoadingLabel : getMetaLeft(schedule)}
        </Text>
        <Text fontSize="sm" color="fg.muted" textAlign="right" lineClamp={1}>
          {isSummaryLoading ? '' : getMetaRight(schedule)}
        </Text>
      </Flex>
    </Box>
  )
}

export const SchedulesListPageView = ({
  schedules,
  isLoading = false,
  loadingScheduleIds,
  error = null,
  strings,
  canCreate = true,
  onCreate,
  onScheduleClick,
  getScheduleHref,
  onDeleteSchedule,
  getScheduleStatus,
  getScheduleDescription,
  getMetaLeft = (schedule) => schedule.time_zone,
  getMetaRight,
  createIcon,
  deleteIcon,
  totalCount,
  currentPage: controlledCurrentPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
}: SchedulesListPageViewProps) => {
  const { text } = useScheduleI18n()
  const [pendingDeleteSchedule, setPendingDeleteSchedule] =
    useState<Schedule | null>(null)
  const {
    pageSize: internalPageSize,
    setPageSize: setInternalPageSize,
    currentPage: internalCurrentPage,
    setCurrentPage: setInternalCurrentPage,
  } = useScheduleListPagination({ initialPageSize: 25 })

  const isExternallyPaginated =
    controlledCurrentPage !== undefined ||
    controlledPageSize !== undefined ||
    !!onPageChange ||
    !!onPageSizeChange ||
    totalCount !== undefined

  const resolvedPageSize = controlledPageSize ?? internalPageSize
  const resolvedCurrentPage = controlledCurrentPage ?? internalCurrentPage
  const resolvedTotalCount = totalCount ?? schedules.length
  const toolbarT: TranslateFn = useCallback(
    (key, opts) =>
      text(
        key,
        {
          'schedule.list.pagination.resultsPerPage': 'Results per page:',
          'schedule.list.pagination.all': 'All',
          'schedule.list.pagination.previousPage': 'Previous page',
          'schedule.list.pagination.nextPage': 'Next page',
          'schedule.list.pagination.jumpToPage': 'Jump to page {{page}}',
        }[key] ?? key,
        opts as Record<string, string | number> | undefined,
      ),
    [text],
  )
  const searchPlaceholderLabel =
    searchPlaceholder ??
    text('schedule.list.search.placeholder', 'Search schedules by name')
  const searchAriaLabel = text('schedule.list.search.aria', 'Search schedules')
  const errorTitle = text('schedule.list.grid.errorTitle', 'Error')
  const notAvailableLabel = text('schedule.list.common.notAvailable', 'N/A')
  const deleteScheduleTitle = text(
    'schedule.list.deleteTitle',
    'Delete schedule',
  )
  const resolvedGetMetaRight =
    getMetaRight ??
    ((schedule: Schedule) =>
      formatUpdatedAt(schedule.updated_at, notAvailableLabel))
  const isSearchActive = searchValue.trim().length > 0
  const emptyStateTitle = isSearchActive
    ? (strings.emptySearchTitle ??
      text('schedule.list.search.emptyTitle', 'No schedules match your search'))
    : strings.emptyTitle
  const emptyStateDescription = isSearchActive
    ? (strings.emptySearchDescription ??
      text(
        'schedule.list.search.emptyDescription',
        'Try a different search term.',
      ))
    : strings.emptyDescription

  const setPageSize = useCallback(
    (nextPageSize: ScheduleListPageSize) => {
      if (onPageSizeChange) {
        onPageSizeChange(nextPageSize)
      } else {
        setInternalPageSize(nextPageSize)
      }
    },
    [onPageSizeChange, setInternalPageSize],
  )

  const setCurrentPage = useCallback(
    (nextPage: number) => {
      if (onPageChange) {
        onPageChange(nextPage)
      } else {
        setInternalCurrentPage(nextPage)
      }
    },
    [onPageChange, setInternalCurrentPage],
  )

  const setCurrentPageDispatch = useCallback<Dispatch<SetStateAction<number>>>(
    (nextPage) => {
      const resolvedNextPage =
        typeof nextPage === 'function'
          ? nextPage(resolvedCurrentPage)
          : nextPage
      setCurrentPage(resolvedNextPage)
    },
    [resolvedCurrentPage, setCurrentPage],
  )

  useScheduleListPageClamp({
    pageSize: resolvedPageSize,
    currentPage: resolvedCurrentPage,
    setCurrentPage: setCurrentPageDispatch,
    totalCount: resolvedTotalCount,
    isLoading,
    hasError: !!error,
  })

  const visibleSchedules = useMemo(() => {
    if (isExternallyPaginated) {
      return schedules
    }

    if (resolvedPageSize === 'All') {
      return schedules
    }

    const startIndex = (resolvedCurrentPage - 1) * resolvedPageSize
    const endIndex = startIndex + resolvedPageSize
    return schedules.slice(startIndex, endIndex)
  }, [isExternallyPaginated, schedules, resolvedCurrentPage, resolvedPageSize])

  const handleRequestDeleteSchedule = (schedule: Schedule) => {
    setPendingDeleteSchedule(schedule)
  }

  const rootRef = useRef<HTMLDivElement>(null)
  const deletedScheduleRef = useRef<{ id: ScheduleId; index: number } | null>(
    null,
  )

  const handleConfirmDeleteSchedule = () => {
    if (pendingDeleteSchedule && onDeleteSchedule) {
      deletedScheduleRef.current = {
        id: pendingDeleteSchedule.id,
        index: visibleSchedules.findIndex(
          (schedule) => schedule.id === pendingDeleteSchedule.id,
        ),
      }
      onDeleteSchedule(pendingDeleteSchedule)
    }
    setPendingDeleteSchedule(null)
  }

  // The deleted card held focus (its delete button); once it's gone, move
  // focus to the card that took its place instead of leaving it on <body>.
  useEffect(() => {
    const deleted = deletedScheduleRef.current
    if (!deleted) return
    if (visibleSchedules.some((schedule) => schedule.id === deleted.id)) return
    deletedScheduleRef.current = null

    const activeElement = document.activeElement
    if (activeElement && activeElement !== document.body) return

    const root = rootRef.current
    if (!root) return
    const neighbor =
      visibleSchedules[deleted.index] ?? visibleSchedules[deleted.index - 1]
    const card = neighbor
      ? root.querySelector(`[data-schedule-card-id="${neighbor.id}"]`)
      : null
    const focusTarget =
      card?.querySelector<HTMLElement>('a[href], button') ??
      root.querySelector<HTMLElement>('input, button')
    focusTarget?.focus()
  }, [visibleSchedules])

  const hasLoadedResults = resolvedTotalCount > 0 && !isLoading && !error
  const showListToolbar = !error && (hasLoadedResults || !!onSearchChange)

  return (
    <Box
      ref={rootRef}
      w="full"
      minW={0}
      h="full"
      display="flex"
      flexDirection="column"
    >
      <Box
        bg="bg.default"
        borderBottomWidth="1px"
        borderColor="border.default"
        w="full"
        minW={0}
        flexShrink={0}
      >
        <Flex h="72px" px={6} align="center" justify="space-between" minW={0}>
          <Box minW={0} flex={1}>
            <Heading
              as="h1"
              fontSize="20px"
              fontWeight="extrabold"
              lineHeight="28px"
              color="fg.default"
              m={0}
            >
              {strings.title}
            </Heading>
            <Text fontSize="sm" color="fg.subtle" mt={1} m={0}>
              {strings.subtitle}
            </Text>
          </Box>
          <Button
            onClick={onCreate}
            colorPalette="info"
            color="blue.contrast"
            h="40px"
            px={4}
            borderRadius="l2"
            disabled={!canCreate}
            fontWeight="semibold"
            fontSize="sm"
            gap={2}
            flexShrink={0}
            css={tokenizedSvgColorStyles}
          >
            {createIcon ?? <PlusIcon size={18} strokeWidth={1.5} />}
            {strings.newSchedule}
          </Button>
        </Flex>

        {showListToolbar && (
          <Flex
            justify="space-between"
            align="center"
            gap={4}
            flexWrap="wrap"
            mx={5}
            borderTopWidth={hasLoadedResults ? 0 : '1px'}
            borderColor="border.default"
            pt={hasLoadedResults ? 0 : 4}
            pb={4}
          >
            {hasLoadedResults ? (
              <Box flex="1" minW="240px">
                <ListToolbar
                  pageSize={resolvedPageSize}
                  onPageSizeChange={(nextPageSize) => {
                    setPageSize(nextPageSize)
                    if (resolvedCurrentPage !== 1) {
                      setCurrentPage(1)
                    }
                  }}
                  currentPage={resolvedCurrentPage}
                  totalCount={resolvedTotalCount}
                  onPageChange={setCurrentPage}
                  isLoading={isLoading}
                  showToolbar
                  pageSizeOptions={SCHEDULE_PAGE_SIZE_OPTIONS}
                  showAllOption
                  maxAllThreshold={Number.MAX_SAFE_INTEGER}
                  t={toolbarT}
                  i18nPrefix="schedule.list.pagination"
                />
              </Box>
            ) : (
              <Box />
            )}

            {onSearchChange && (
              <Box
                w={{ base: 'full', md: '280px' }}
                ml="auto"
                pt={hasLoadedResults ? 4 : 0}
              >
                <Box position="relative">
                  <Box
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    pointerEvents="none"
                    color="fg.muted"
                  >
                    <SearchIcon size={16} />
                  </Box>
                  <Input
                    value={searchValue}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={searchPlaceholderLabel}
                    aria-label={searchAriaLabel}
                    size="sm"
                    pl={10}
                    bg="bg.subtle"
                    borderColor="border.default"
                    _placeholder={{ color: 'fg.subtle' }}
                    _hover={{ borderColor: 'border.emphasized' }}
                    _focus={{
                      borderColor: 'brand.solid',
                      boxShadow: `0 0 0 1px ${tokenVar('brand.solid')}`,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Flex>
        )}
      </Box>

      <Box py={6} px={6} w="full" flex={1} minH={0} overflowY="auto">
        {isLoading ? (
          <Flex align="center" justify="center" h="full" minH="420px">
            <Spinner size="xl" />
          </Flex>
        ) : error ? (
          <Alert.Root status="error" p={4}>
            <Alert.Indicator />
            <Box>
              <Text fontWeight="bold">
                {formatErrorCode(error.code, errorTitle)}
              </Text>
              <Text>{error.message}</Text>
            </Box>
          </Alert.Root>
        ) : schedules.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={16}
            px={4}
            textAlign="center"
            maxW="362px"
            mx="auto"
          >
            <Text
              fontSize="2xl"
              fontWeight="semibold"
              color="fg.default"
              mb={2}
            >
              {emptyStateTitle}
            </Text>
            <Text fontSize="sm" color="fg.muted" lineHeight="tall">
              {emptyStateDescription}
            </Text>
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6} w="full">
            {visibleSchedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                isSummaryLoading={loadingScheduleIds?.has(schedule.id) ?? false}
                summaryLoadingLabel={strings.loading}
                deleteLabel={strings.deleteSchedule}
                onScheduleClick={onScheduleClick}
                getScheduleHref={getScheduleHref}
                onDeleteSchedule={
                  onDeleteSchedule ? handleRequestDeleteSchedule : undefined
                }
                getScheduleStatus={getScheduleStatus}
                getScheduleDescription={getScheduleDescription}
                getMetaLeft={getMetaLeft}
                getMetaRight={resolvedGetMetaRight}
                deleteIcon={deleteIcon}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
      <DeleteConfirmationModal
        isOpen={pendingDeleteSchedule !== null}
        onClose={() => setPendingDeleteSchedule(null)}
        onConfirm={handleConfirmDeleteSchedule}
        title={deleteScheduleTitle}
        itemName={pendingDeleteSchedule?.name}
      />
    </Box>
  )
}
